import { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  type DmParticipant,
  useConversation,
} from '../../hooks/useDirectMessages';
import styles from './directMessages.module.scss';
import { formatMessageTime } from './dmFormat';

interface Props {
  convId: string;
  otherUser?: DmParticipant | null;
  onBack: () => void;
}

/**
 * TRIL-394: the live chat view for one conversation. Bubbles align by sender,
 * new inbound messages append live (WS), and the thread is marked read on open.
 * The recipient is derived from the convId (canonical sorted pair) so the view is
 * self-sufficient even on a deep link before the inbox list has loaded.
 */
export function ConversationThread({ convId, otherUser, onBack }: Props) {
  const { user } = useAuth();
  const myId = user?.id;
  const recipientId = convId.split('#').find((id) => id && id !== myId) ?? '';

  const {
    messages,
    loading,
    sending,
    otherTyping,
    send,
    notifyTyping,
    markRead,
  } = useConversation(convId);
  const [draft, setDraft] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // Mark read on open AND whenever new messages arrive while the thread is open, so the unread
  // badge clears even for messages received mid-conversation (TRIL-505 parity with mobile).
  useEffect(() => {
    void markRead();
  }, [markRead, messages.length]);

  // Keep the newest message in view.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const submit = async () => {
    const body = draft.trim();
    if (!body || !recipientId) return;
    setDraft('');
    await send(recipientId, body);
  };

  const name = otherUser?.username ?? 'Conversation';

  return (
    <div className={styles.chat}>
      <header className={styles.chatHeader}>
        <button
          type="button"
          className={styles.back}
          onClick={onBack}
          aria-label="Back to conversations"
        >
          ←
        </button>
        <span className={styles.avatarSmall} aria-hidden="true">
          {name.charAt(0).toUpperCase()}
        </span>
        <span className={styles.chatName}>{name}</span>
        {/* TRIL-396: presence dot — hidden when online is unknown (null / brand-new thread). */}
        {otherUser?.online != null && (
          <span
            className={`${styles.presenceDot} ${otherUser.online ? styles.online : styles.offline}`}
            title={otherUser.online ? 'Online' : 'Offline'}
            role="img"
            aria-label={
              otherUser.online ? `${name} is online` : `${name} is offline`
            }
          />
        )}
      </header>

      <div className={styles.messages}>
        {loading && messages.length === 0 ? (
          <p className={styles.hint}>Loading…</p>
        ) : messages.length === 0 ? (
          <p className={styles.hint}>No messages yet — say hello.</p>
        ) : (
          messages.map((m) => (
            <div
              key={m.messageId}
              className={`${styles.bubbleRow} ${m.senderId === myId ? styles.mine : styles.theirs}`}
            >
              <div className={styles.bubble}>
                <span className={styles.bubbleBody}>{m.body}</span>
                <time className={styles.bubbleTime} dateTime={m.createdAt}>
                  {formatMessageTime(m.createdAt)}
                </time>
              </div>
            </div>
          ))
        )}
        {otherTyping && (
          <div className={styles.typing} aria-live="polite">
            {name} is typing…
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <form
        className={styles.composer}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <textarea
          className={styles.input}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (recipientId) notifyTyping(recipientId);
          }}
          placeholder="Type a message…"
          rows={1}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void submit();
            }
          }}
        />
        <button
          type="submit"
          className={styles.sendBtn}
          disabled={sending || !draft.trim()}
        >
          Send
        </button>
      </form>
    </div>
  );
}
